/**
 * API Service
 * Handles token loading and API creation
 *
 * Note: this module previously imported `SwapKitApi` from
 * `@doritokit/api`, but the actual implementation routes through
 * `window.createApi` (provided by the legacy SwapKit UMD bundle in
 * `public/index.html`) and never used the named import. Dropping the
 * import shaves @doritokit/api off the synchronous boot payload.
 */
import { balanceApi } from "../components/contexts/SKClientProviderManager";
import { config } from "../config/environment.js";
import { createLogger } from '../utils/logger.js';
import { detectLiveXmrSwap } from "../components/apps/winbit32/helpers/xmrSwapCapability";
import { MONERO_DECIMALS, XMR_CHAIN, XMR_IDENTIFIER } from "../components/apps/winbit32/helpers/moneroAddress";

const log = createLogger('API');

const resolveBalanceApiKey = () =>
	config.api.ankr || config.api.alchemy || config.api.covalent;

/**
 * API configuration flags
 */
const API_CONFIG = {
	USE_DORITO_ONLY: true, // Set to true to use only Dorito API
	USE_SWAPKIT_ONLY: false, // Set to true to use only SwapKit API
	USE_BOTH: false // Set to true to use both APIs with preference logic
};

/**
 * Creates an API instance for token pricing and data
 * @param {Object} options - API options
 * @param {string} options.apiKey - API key for authentication
 * @returns {Object|null} API instance or null if creation failed
 */
export const createApi = ({ apiKey }) => {
	try {
		log.debug("Creating API", apiKey ? "key provided" : "no key");
		
		if (window.createApi) {
			const windowApi = window.createApi({ apiKey });
			log.debug("API created via window.createApi");
			return windowApi;
		} else {
			log.error("window.createApi is not available");
			
			// Create a fallback API if window.createApi is not available
			log.debug("Creating fallback API");
			return {
				getPrice: async () => ({ price: 0 }),
				getPrices: async () => ({}),
				getTokens: async () => ([]),
				getProviders: async () => ([]),
				isValid: true
			};
		}
	} catch (error) {
		log.error("Error creating API:", error);
		return null;
	}
};

/**
 * Loads tokens from the API
 * @param {Object} globalTokens - Existing tokens to check
 * @returns {Promise<Array>} Array of tokens
 */
export const loadTokens = async (globalTokens, dispatch) => {
	// If tokens are already loaded, return them
	if (globalTokens && Array.isArray(globalTokens) && globalTokens.length > 1) {
		log.debug("Returning existing tokens:", globalTokens.length);
		return globalTokens;
	}
	
	try {
		const doritoBaseUrl = "https://kit.winbit32.com/v1/";
		const swapkitBaseUrl = "https://api.swapkit.dev/";
		const swapkitApiKey = config.api.swapkit;
		const fetch = require("fetch-retry")(global.fetch);
		
		let allProviders = [];
		
		if (API_CONFIG.USE_DORITO_ONLY) {
			log.debug("Using Dorito API only");
			// Fetch providers from Dorito only
			const providerResponse = await fetch(doritoBaseUrl + "providers");
			if (providerResponse.status !== 200) {
				throw new Error(`Dorito API returned status ${providerResponse.status}`);
			}
			const providers = await providerResponse.json();
			allProviders = providers.sort((a, b) => {
				if (a.provider === "THORSWAP" || b.provider === "MAYA") return -1;
				if (b.provider === "THORSWAP" || a.provider === "MAYA") return 1;
				return a.provider < b.provider ? -1 : 1;
			});
			log.debug("Dorito providers loaded:", allProviders.length);
		} else if (API_CONFIG.USE_SWAPKIT_ONLY) {
			log.debug("Using SwapKit API only");
			// Fetch providers from SwapKit only
			const providerResponse = await fetch(swapkitBaseUrl + "providers", {
				headers: {
					"x-api-key": swapkitApiKey
				}
			});
			if (providerResponse.status !== 200) {
				throw new Error(`SwapKit API returned status ${providerResponse.status}`);
			}
			const providers = await providerResponse.json();
			allProviders = providers.sort((a, b) => {
				if (a.provider === "THORSWAP" || b.provider === "MAYA") return -1;
				if (b.provider === "THORSWAP" || a.provider === "MAYA") return 1;
				return a.provider < b.provider ? -1 : 1;
			});
			log.debug("SwapKit providers loaded:", allProviders.length);
		} else {
			// This would be the complex logic for using both APIs
			throw new Error("Both API mode not implemented yet");
		}
		
		dispatch({ type: "SET_PROVIDERS", providers: allProviders });
		
		// Fetch tokens from each provider
		log.debug("Fetching tokens for each provider");
		const tokensResponse = await Promise.all(
			allProviders.map(async (provider) => {
				// Streaming swap variants (`THORCHAIN_STREAMING`,
				// `MAYACHAIN_STREAMING`, etc.) share the same token list as
				// their parent chain provider and the `/tokens` endpoint
				// returns 400 for them. Skip the fetch and let the parent
				// provider's tokens cover the streaming variant.
				if (typeof provider.provider === 'string'
					&& provider.provider.endsWith('_STREAMING')) {
					log.debug(`Skipping token fetch for streaming variant ${provider.provider}`);
					return [];
				}
				try {
					let tokenResponse;

					if (API_CONFIG.USE_DORITO_ONLY) {
						tokenResponse = await fetch(doritoBaseUrl + `tokens?provider=${provider.provider}`);
					} else if (API_CONFIG.USE_SWAPKIT_ONLY) {
						tokenResponse = await fetch(swapkitBaseUrl + `tokens?provider=${provider.provider}`, {
							headers: {
								"x-api-key": swapkitApiKey
							}
						});
					}

					if (tokenResponse.status !== 200) {
						throw new Error(`API returned status ${tokenResponse.status}`);
					}
					
					const tokenData = await tokenResponse.json();
					
					if (!tokenData.tokens) {
						log.warn(`No tokens found for provider: ${provider.provider}`);
						return [];
					}
					
					log.debug(`Fetched ${tokenData.tokens.length} tokens for ${provider.provider}`);
					return tokenData.tokens
						.map(token => ({
							...token,
							logoURI: token.identifier?.includes("/") ? 
								token.logoURI.split("/").slice(0,-1).join("/") + "." + token.logoURI.split("/").pop() :
								token.logoURI,
							provider: provider.provider,
							source: API_CONFIG.USE_DORITO_ONLY ? 'dorito' : 'swapkit'
						}))
						.filter(token => token.chain !== "BNB");
				} catch (error) {
					log.error(`Failed to fetch tokens for provider ${provider.provider}:`, error);
					return [];
				}
			})
		);
		
		const sortedTokens = tokensResponse.flat().sort((a, b) => {
			if (a.shortCode || b.shortCode) return a.shortCode ? -1 : 1;
			if (a.chain === a.ticker || b.chain === b.ticker) return a.chain === a.ticker ? -1 : 1;
			return a.chain < b.chain ? -1 : 1;
		});
		
		// Manually add ZEC chain and tokens
		log.debug("Manually adding ZEC chain and tokens");
		
		// Add ZEC token if not already in the list
		let hasZcash = sortedTokens.some(token => token.chain === "ZEC" && token.ticker === "ZEC" && token.identifier === "ZEC.ZEC");
		if(hasZcash){
			// Remove incorrect ZEC.ZEC token and set hasZcash to false
			const zecIndex = sortedTokens.findIndex(token => token.identifier === "ZEC.ZEC");
			if (zecIndex !== -1) {
				sortedTokens.splice(zecIndex, 1);
			}
			hasZcash = false;
		}

		// Manually add DASH chain native token if missing (for Maya Protocol support)
		let hasDash = sortedTokens.some(token => token.chain === "DASH" && token.ticker === "DASH" && token.identifier === "DASH.DASH");
		if (!hasDash) {
			log.debug("Adding DASH.DASH token to token list");
			sortedTokens.push({
				chain: "DASH",
				symbol: "DASH",
				ticker: "DASH",
				name: "Dash",
				identifier: "DASH.DASH",
				decimals: 8,
				logoURI: "https://cryptologos.cc/logos/dash-dash-logo.svg?v=032",
				provider: "MAYA",
				shortCode: 'DASH.DASH',
				isSynthetic: false,
				isNative: true,
				chainId: "DASH",
				source: 'manual'
			});
		}

		if (!hasZcash) {
			log.debug("Adding ZEC.ZEC token to token list");
			sortedTokens.push({
				chain: "ZEC",
				symbol: "ZEC",
				ticker: "ZEC",
				name: "ZEC",
				identifier: "ZEC.ZEC",
				decimals: 8,
				logoURI: "https://upload.wikimedia.org/wikipedia/commons/c/c6/Zcash_logo_%282024%E2%80%93present%29.svg",
				provider: "MAYA",
				shortCode: 'ZEC.ZEC',
				isSynthetic: false,
				isNative: true,
				isShielded: false,
				isSynthetic: false,
				chainId: "ZEC",
				source: 'manual'
			});
		}
		
		// Add ZEC (Shielded) token for private transactions
		const hasZcashShielded = sortedTokens.some(token => token.chain === "ZEC" && token.ticker === "ZEC" && token.identifier === "ZEC.ZEC_SHIELDED");
		if (!hasZcashShielded) {
			log.debug("Adding ZEC (Shielded) token to token list");
			sortedTokens.push({
				chain: "ZEC",
				symbol: "ZEC_SHIELDED",
				ticker: "ZEC_SHIELDED",
				name: "ZEC (Shielded)",
				identifier: "ZEC.ZEC_SHIELDED",
				decimals: 8, // Ensure decimals is set
				decimal: 8, // Also set decimal for compatibility
				logoURI: "https://upload.wikimedia.org/wikipedia/commons/c/c6/Zcash_logo_%282024%E2%80%93present%29.svg",
				provider: "MAYA",
				shortCode: 'ZEC.ZEC_SHIELDED',
				isSynthetic: false,
				isNative: true,
				isShielded: true, // Flag to indicate this is the shielded variant
				isSynthetic: false,
				chainId: "ZEC",
				source: 'manual'
			});
		}
		
		// Ensure NEAR.NEAR has correct decimals (24) — remove any provider-supplied
		// version that may have wrong decimals (e.g. 18), then add canonical entry.
		const nearIdx = sortedTokens.findIndex(token => token.chain === "NEAR" && token.identifier === "NEAR.NEAR");
		if (nearIdx !== -1) {
			const existing = sortedTokens[nearIdx];
			if (existing.decimals !== 24) {
				log.debug(`Replacing NEAR.NEAR with wrong decimals (${existing.decimals}) → 24`);
				sortedTokens.splice(nearIdx, 1);
			}
		}
		if (!sortedTokens.some(token => token.chain === "NEAR" && token.identifier === "NEAR.NEAR")) {
			log.debug("Adding NEAR.NEAR token to token list");
			sortedTokens.push({
				chain: "NEAR",
				symbol: "NEAR",
				ticker: "NEAR",
				name: "NEAR Protocol",
				identifier: "NEAR.NEAR",
				decimals: 24,
				decimal: 24,
				logoURI: "https://assets.coingecko.com/coins/images/10365/standard/near.jpg",
				provider: "MAYA",
				shortCode: 'NEAR.NEAR',
				isSynthetic: false,
				isNative: true,
				chainId: "NEAR",
				source: 'manual'
			});
		}

		// Monero (XMR) — only surface it once a live XMR pool is detected on
		// THORChain/Maya. Until then it stays out of the chooser entirely (no
		// dead UI). Detection is cached + fails closed, and is wrapped so it can
		// never break token loading.
		try {
			// Cap the wait so a slow/hung pool probe can't block token loading.
			// If detection doesn't resolve in time the cache still warms in the
			// background and XMR appears on the next token load.
			const xmrStatus = await Promise.race([
				detectLiveXmrSwap(),
				new Promise(resolve => setTimeout(() => resolve({ live: false, provider: null }), 4000)),
			]);
			if (xmrStatus.live && !sortedTokens.some(token => token.identifier === XMR_IDENTIFIER)) {
				log.info(`Adding ${XMR_IDENTIFIER} token to token list (live via ${xmrStatus.provider})`);
				sortedTokens.push({
					chain: XMR_CHAIN,
					symbol: "XMR",
					ticker: "XMR",
					name: "Monero",
					identifier: XMR_IDENTIFIER,
					decimals: MONERO_DECIMALS,
					decimal: MONERO_DECIMALS,
					logoURI: "https://www.getmonero.org/press-kit/symbols/monero-symbol-on-white-480.png",
					// Provider must match the live venue so it routes correctly and
					// passes the TokenChooser provider filter (THOR*/MAYA*).
					provider: xmrStatus.provider === 'MAYACHAIN' ? 'MAYA' : 'THORCHAIN',
					shortCode: XMR_IDENTIFIER,
					isSynthetic: false,
					isNative: true,
					chainId: XMR_CHAIN,
					source: 'manual',
				});
			}
		} catch (err) {
			log.warn("XMR swap capability check skipped:", err?.message || err);
		}

		log.info("Tokens loaded successfully:", sortedTokens.length);
		return sortedTokens;
	} catch (error) {
		log.error("Error loading tokens:", error);
		return [];
	}
};

/**
 * Loads providers and tokens and creates API if needed
 * @param {Object} state - Current state with globalTokens and globalApi
 * @param {Function} dispatch - Dispatch function to update state
 * @param {Object} tokensLoadedRef - Ref to track if tokens are loaded
 * @returns {Promise<Array>} Array of tokens
 */
export const loadProvidersAndTokens = async (state, dispatch, tokensLoadedRef) => {
	// If tokens are already loaded, return
	if (tokensLoadedRef.current === true && 
		state.globalTokens && 
		state.globalTokens.length > 1 &&
		Array.isArray(state.globalTokens) && 
		state.globalTokens.length > 1) {
		log.debug("Tokens already loaded:", state.globalTokens.length);
		return state.globalTokens;
	}
	
	try {
		// Load tokens
		const tokens = await loadTokens(state.globalTokens, dispatch);
		
		if (tokens && Array.isArray(tokens) && tokens.length > 1) {
			log.info("Tokens loaded successfully:", tokens.length);

			log.debug("Creating global API");
			const api = balanceApi({
				tokens,
				apiKey: resolveBalanceApiKey(),
			});
			

			// Update state with tokens and API
			dispatch({ 
				type: "SET_GLOBAL_TOKENS", 
				tokens,
				api: api
			});
			
			// Set the tokens loaded flag
			if (tokensLoadedRef) {
				tokensLoadedRef.current = tokens;
			}
			
			return tokens;
		} else {
			log.error("Tokens load failed - returned invalid data:", tokens);
			return [];
		}
	} catch (error) {
		log.error("Token loading error:", error);
		return [];
	}
};

/**
 * Waits for tokens to be loaded
 * @param {Object} state - Current state
 * @param {Function} loadTokensFunc - Function to load tokens
 * @param {Object} tokensLoadedRef - Ref to track if tokens are loaded
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<void>}
 */
export const waitForTokens = async (state, loadTokensFunc, tokensLoadedRef, maxRetries = 20) => {
	log.debug("Waiting for tokens to load");

	// If tokens are already loaded and valid, return immediately
	if (tokensLoadedRef.current &&
		state.globalTokens && 
		Array.isArray(state.globalTokens) && 
		state.globalTokens.length > 1) {
		return;
	}

	// If tokens aren't loaded yet, try to load them
	if (loadTokensFunc && typeof loadTokensFunc === 'function') {
		try {
			const tokens = await loadTokensFunc();
			
			// If tokens were loaded successfully, return immediately
			if (tokens && Array.isArray(tokens) && tokens.length > 0) {
				tokensLoadedRef.current = tokens;
				return;
			}
		} catch (error) {
			log.warn("Failed initial attempt to load tokens:", error);
		}
	}

	// Wait for tokens to be loaded
	for (let i = 0; i < maxRetries; i++) {
		// Check if tokens are loaded and valid
		if (tokensLoadedRef.current &&
			(state.globalTokens && Array.isArray(state.globalTokens) && state.globalTokens.length > 1)) {
			
			// If tokens exist but flag isn't set, set it now
			if (!tokensLoadedRef.current && state.globalTokens && state.globalTokens.length > 1) {
				tokensLoadedRef.current = state.globalTokens;
			}
			
			return;
		}

		// After a few retries, try loading them again
		if (i % 3 === 2 && loadTokensFunc && typeof loadTokensFunc === 'function') {
			try {
				log.debug(`Retry ${i+1}/${maxRetries}: attempting to load tokens`);
				const tokens = await loadTokensFunc();
				
				// If tokens were loaded successfully, return immediately
				if (tokens && Array.isArray(tokens) && tokens.length > 0) {
					tokensLoadedRef.current = tokens;
					return;
				}
			} catch (error) {
				log.warn(`Retry ${i+1}/${maxRetries}: failed to load tokens:`, error);
			}
		}

		// Check if global API is available
		if (state.globalApi) {
			if (state.globalTokens && Array.isArray(state.globalTokens) && state.globalTokens.length > 0) {
				// If API is available and tokens exist but not marked as loaded, mark them as loaded
				if (!tokensLoadedRef.current) {
					tokensLoadedRef.current = state.globalTokens;
				}
				return;
			}
		}

		// Wait before checking again
		await new Promise(resolve => setTimeout(resolve, 500));
	}

	// If we get here, tokens failed to load after all retries
	log.warn("Tokens failed to load after maximum retries");

	// If globalApi is available, we can continue despite tokens not being fully loaded
	if (state.globalApi) {
		log.debug("Global API available despite token loading issues, proceeding");
		return;
	}

	throw new Error("Failed to load tokens after multiple attempts");
}; 